import React, { useEffect, useState, useMemo } from "react";
import api, { formatErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { canEdit } from "../lib/permissions";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast, Toaster } from "sonner";
import { fmtMoney, exportToPDF, exportToExcel } from "../lib/exports";
import { FileDown, FileSpreadsheet, Trash2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().slice(0, 10);

export default function HistorialGastos() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const puedeEditar = canEdit(user, "historial_gastos");
  const [fechaIni, setFechaIni] = useState(monthStart());
  const [fechaFin, setFechaFin] = useState(today());
  const [sucursalId, setSucursalId] = useState("");
  const [categoria, setCategoria] = useState("");
  const [metodoPago, setMetodoPago] = useState("");
  const [sucursales, setSucursales] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/sucursales"), api.get("/gastos/categorias")]).then(([rs, rc]) => {
      setSucursales(rs.data); setCategorias(rc.data);
    });
  }, []);

  const cargar = async () => {
    setLoading(true);
    try {
      const params = { fecha_ini: fechaIni, fecha_fin: fechaFin, tipo: "diario" };
      if (sucursalId) params.sucursal_id = sucursalId;
      if (categoria) params.categoria = categoria;
      if (metodoPago) params.metodo_pago = metodoPago;
      const { data } = await api.get("/gastos", { params });
      setRows(data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargar(); }, []);

  const sucName = (id) => sucursales.find((s) => s.id === id)?.nombre || "—";
  const total = useMemo(() => rows.reduce((s, r) => s + (r.importe || 0), 0), [rows]);

  const eliminar = async (id) => {
    if (!window.confirm("¿Eliminar gasto?")) return;
    try { await api.delete(`/gastos/${id}`); toast.success("Eliminado"); cargar(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  const expPDF = () => {
    const data = rows.map((r) => ({ ...r, sucursal: sucName(r.sucursal_id), importe_fmt: fmtMoney(r.importe), metodo_fmt: (r.metodo_pago || "efectivo") }));
    exportToPDF({
      title: "Historial de Gastos",
      subtitle: `${fechaIni} a ${fechaFin}`,
      columns: [
        { header: "Fecha", key: "fecha" },
        { header: "Sucursal", key: "sucursal" },
        { header: "Categoría", key: "categoria" },
        { header: "Método", key: "metodo_fmt" },
        { header: "Comentario", key: "comentario" },
        { header: "Usuario", key: "usuario_email" },
        { header: "Importe", key: "importe_fmt" },
      ],
      rows: data,
      summary: [{ label: "Total", value: fmtMoney(total) }],
    });
  };
  const expXLS = () => {
    exportToExcel({
      title: "Historial_Gastos",
      rows: rows.map((r) => ({
        Fecha: r.fecha, Sucursal: sucName(r.sucursal_id), Categoría: r.categoria,
        Método: r.metodo_pago || "efectivo",
        Importe: r.importe, Comentario: r.comentario, Usuario: r.usuario_email,
      })),
    });
  };

  return (
    <div className="space-y-6">
      <Toaster richColors position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.15em] text-blue-600">Consultas</div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight mt-1">{isAdmin ? "Historial de Gastos" : "Mis Gastos"}</h1>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={expPDF}><FileDown className="w-4 h-4 mr-2" />PDF</Button>
            <Button variant="outline" onClick={expXLS}><FileSpreadsheet className="w-4 h-4 mr-2" />Excel</Button>
          </div>
        )}
      </div>

      <Card className={`p-5 grid grid-cols-2 ${isAdmin ? "md:grid-cols-6" : "md:grid-cols-5"} gap-3`}>
        <Field label="Desde"><Input type="date" value={fechaIni} onChange={(e) => setFechaIni(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Hasta"><Input type="date" value={fechaFin} onChange={(e) => setFechaFin(e.target.value)} className="bg-blue-50 border-blue-200" /></Field>
        <Field label="Sucursal">
          <Select value={sucursalId || "all"} onValueChange={(v) => setSucursalId(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {sucursales.map((s) => <SelectItem key={s.id} value={s.id}>{s.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Categoría">
          <Select value={categoria || "all"} onValueChange={(v) => setCategoria(v === "all" ? "" : v)}>
            <SelectTrigger className="bg-blue-50 border-blue-200"><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todas</SelectItem>
              {categorias.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        {isAdmin && (
          <Field label="Método">
            <Select value={metodoPago || "all"} onValueChange={(v) => setMetodoPago(v === "all" ? "" : v)}>
              <SelectTrigger className="bg-blue-50 border-blue-200" data-testid="hist-gasto-metodo"><SelectValue placeholder="Todos" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="efectivo">Efectivo</SelectItem>
                <SelectItem value="transferencia">Transferencia</SelectItem>
                <SelectItem value="tarjeta">Tarjeta</SelectItem>
              </SelectContent>
            </Select>
          </Field>
        )}
        <div className="flex items-end">
          <Button onClick={cargar} className="bg-blue-600 hover:bg-blue-700 text-white w-full h-10">Filtrar</Button>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-slate-100 border-b border-slate-200">
              <tr>
                <Th>Fecha</Th><Th>Sucursal</Th><Th>Categoría</Th>
                {isAdmin && <Th>Método</Th>}
                <Th>Comentario</Th><Th>Usuario</Th><Th right>Importe</Th>
                {puedeEditar && <Th>Acciones</Th>}
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={isAdmin ? 8 : 6} className="px-4 py-8 text-center text-slate-500">Cargando…</td></tr>}
              {!loading && !rows.length && <tr><td colSpan={isAdmin ? 8 : 6} className="px-4 py-8 text-center text-slate-500">Sin registros</td></tr>}
              {rows.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50">
                  <Td>{r.fecha}</Td>
                  <Td>{sucName(r.sucursal_id)}</Td>
                  <Td>{r.categoria}</Td>
                  {isAdmin && (
                    <Td>
                      <MetodoBadge metodo={r.metodo_pago || "efectivo"} />
                    </Td>
                  )}
                  <Td className="text-xs text-slate-500">{r.comentario}</Td>
                  <Td className="text-xs text-slate-500">{r.usuario_email}</Td>
                  <Td right mono className="font-semibold">{fmtMoney(r.importe)}</Td>
                  {puedeEditar && (
                    <Td>
                      <Button variant="ghost" size="icon" onClick={() => eliminar(r.id)} className="text-red-600 hover:text-red-700">
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </Td>
                  )}
                </tr>
              ))}
            </tbody>
            {rows.length > 0 && (
              <tfoot className="bg-slate-900 text-white">
                <tr>
                  <td className="px-4 py-3 font-semibold" colSpan={isAdmin ? 6 : 5}>TOTAL</td>
                  <td className="px-4 py-3 text-right font-mono font-bold text-lg">{fmtMoney(total)}</td>
                  {puedeEditar && <td></td>}
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-600">{label}</Label>
    <div className="mt-1.5">{children}</div>
  </div>
);
const Th = ({ children, right }) => <th className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-600 ${right ? "text-right" : ""}`}>{children}</th>;
const Td = ({ children, right, mono, className = "" }) => <td className={`px-4 py-2.5 text-sm ${right ? "text-right" : ""} ${mono ? "font-mono" : ""} ${className}`}>{children}</td>;
const MetodoBadge = ({ metodo }) => {
  const styles = {
    efectivo: "bg-emerald-100 text-emerald-800",
    transferencia: "bg-blue-100 text-blue-800",
    tarjeta: "bg-purple-100 text-purple-800",
  };
  const labels = { efectivo: "Efectivo", transferencia: "Transferencia", tarjeta: "Tarjeta" };
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${styles[metodo] || styles.efectivo}`}>
      {labels[metodo] || metodo}
    </span>
  );
};
